/**
 * Converts a color from CSS hex format to CSS rgb format.
 * @param {string} color - Hex color, i.e. #nnn or #nnnnnn
 * @returns {string} A CSS rgb color string
 */
export const hexToRgb = (color) => {
	color = color.slice(1);
	const re = new RegExp(`.{1,${color.length >= 6 ? 2 : 1}}`, "g");
	let colors = color.match(re);
	if (colors && colors[0].length === 1) {
		colors = colors.map((n) => n + n);
	}
	return colors
		? `rgb${colors.length === 4 ? "a" : ""}(${colors
				.map((n, index) => {
					return index < 3
						? parseInt(n, 16)
						: Math.round((parseInt(n, 16) / 255) * 1000) / 1000;
				})
				.join(", ")})`
		: "";
};

/**
 * Converts a color object with type and values to a string.
 * @param {object} color - Decomposed color
 * @param {string} color.type - One of: 'rgb', 'rgba', 'hsl', 'hsla', 'color'
 * @param {array} color.values - [n,n,n] or [n,n,n,n]
 * @returns {string} A CSS color string
 */
export const recomposeColor = (color) => {
	const { type, colorSpace } = color;
	let { values } = color;
	if (type.includes("rgb")) {
		// Only convert the first 3 values to int (i.e. not alpha)
		values = values.map((n, i) => (i < 3 ? parseInt(n, 10) : n));
	} else if (type.includes("hsl")) {
		values[1] = `${values[1]}%`;
		values[2] = `${values[2]}%`;
	}
	if (type.includes("color")) {
		values = `${colorSpace} ${values.join(" ")}`;
	} else {
		values = `${values.join(", ")}`;
	}
	return `${type}(${values})`;
};

/**
 * Returns an object with the type and values of a color.
 *
 * Note: Does not support rgb % values.
 * @param {string} color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla(), color()
 * @returns {object} - A MUI color object: {type: string, values: number[]}
 */
export const decomposeColor = (color) => {
	// Validar que color existe y es un string
	if (!color) {
		throw new Error(
			process.env.NODE_ENV !== "production"
				? "MUI: Color is required."
				: "Color is required"
		);
	}
	// Idempotent
	if (color.type) {
		return color;
	}
	if (typeof color !== "string") {
		throw new Error(
			process.env.NODE_ENV !== "production"
				? `MUI: Color must be a string, received ${typeof color}.`
				: "Color must be a string"
		);
	}
	if (color.charAt(0) === "#") {
		return decomposeColor(hexToRgb(color));
	}
	const marker = color.indexOf("(");
	const type = color.substring(0, marker);
	if (!["rgb", "rgba", "hsl", "hsla", "color"].includes(type)) {
		throw new Error(
			process.env.NODE_ENV !== "production"
				? `MUI: Unsupported \`${color}\` color.
The following formats are supported: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla(), color().`
				: color
		);
	}
	let values = color.substring(marker + 1, color.length - 1);
	let colorSpace;
	if (type === "color") {
		values = values.split(" ");
		colorSpace = values.shift();
		if (values.length === 4 && values[3].charAt(0) === "/") {
			values[3] = values[3].slice(1);
		}
		if (
			!["srgb", "display-p3", "a98-rgb", "prophoto-rgb", "rec-2020"].includes(
				colorSpace
			)
		) {
			throw new Error(
				process.env.NODE_ENV !== "production"
					? `MUI: unsupported \`${colorSpace}\` color space.
The following color spaces are supported: srgb, display-p3, a98-rgb, prophoto-rgb, rec-2020.`
					: colorSpace
			);
		}
	} else {
		values = values.split(",");
	}
	values = values.map((value) => parseFloat(value));
	return {
		type,
		values,
		colorSpace,
	};
};

/**
 * Converts a color from hsl format to rgb format.
 * @param {string} color - HSL color values
 * @returns {string} rgb color values
 */
export const hslToRgb = (color) => {
	color = decomposeColor(color);
	const { values } = color;
	const h = values[0];
	const s = values[1] / 100;
	const l = values[2] / 100;
	const a = s * Math.min(l, 1 - l);
	const f = (n, k = (n + h / 30) % 12) =>
		l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
	let type = "rgb";
	const rgb = [
		Math.round(f(0) * 255),
		Math.round(f(8) * 255),
		Math.round(f(4) * 255),
	];
	if (color.type === "hsla") {
		type += "a";
		rgb.push(values[3]);
	}
	return recomposeColor({
		type,
		values: rgb,
	});
};

/**
 * The relative brightness of any point in a color space,
 * normalized to 0 for darkest black and 1 for lightest white.
 *
 * Formula: https://www.w3.org/TR/WCAG20-TECHS/G17.html#G17-tests
 * @param {string} color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla(), color()
 * @returns {number} The relative brightness of the color in the range 0 - 1
 */
export const getLuminance = (color) => {
	color = decomposeColor(color);
	let rgb =
		color.type === "hsl" || color.type === "hsla"
			? decomposeColor(hslToRgb(color)).values
			: color.values;
	rgb = rgb.map((val) => {
		if (color.type !== "color") {
			val /= 255; // normalized
		}
		return val <= 0.03928 ? val / 12.92 : ((val + 0.055) / 1.055) ** 2.4;
	});

	// Truncate at 3 digits
	return Number(
		(0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]).toFixed(3)
	);
};

export const getTextColor = (color) => {
	// Si el color es undefined, null o vacío, retornar un valor por defecto
	if (!color || typeof color !== "string") {
		return "#000000"; // Por defecto texto negro
	}
	try {
		return getLuminance(color) >= 0.5 ? "#000000" : "#FFFFFF";
	} catch (error) {
		// Si hay algún error al procesar el color, retornar un valor por defecto
		console.warn("Error al calcular el color del texto:", error);
		return "#000000";
	}
};

export const getContrastRatio = (a, b) => {
	try {
		const l1 = getLuminance(a);
		const l2 = getLuminance(b);
		const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1];
		return (hi + 0.05) / (lo + 0.05);
	} catch {
		return 1;
	}
};

/**
 * Color del ítem activo del nav.
 * overMedia: nav transparente sobre el hero — el acento puede coincidir con la foto.
 */
export const getNavActiveColor = ({
	mainColor,
	surface,
	overMedia = false,
	fallback = "#FFFFFF",
} = {}) => {
	if (overMedia) return fallback;
	if (mainColor && surface) {
		try {
			if (getContrastRatio(mainColor, surface) >= 3) return mainColor;
		} catch {
			/* ignore */
		}
	}
	if (surface) return getTextColor(surface);
	return mainColor || fallback;
};

export const getNavActiveChip = ({
	mainColor,
	surface,
	overMedia = false,
} = {}) => {
	if (overMedia) {
		return { backgroundColor: "rgba(255,255,255,0.22)", color: "#FFFFFF" };
	}
	if (mainColor && surface) {
		try {
			if (getContrastRatio(mainColor, surface) >= 3) {
				return { backgroundColor: mainColor, color: getTextColor(mainColor) };
			}
		} catch {
			/* ignore */
		}
	}
	const fg = getTextColor(surface || "#ffffff");
	return {
		backgroundColor: fg === "#FFFFFF" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.08)",
		color: fg,
	};
};
